import React from 'react';
import { useCommercialTerms } from '../../hooks/useCommercialTerms';

export const CommercialTermsTab: React.FC = () => {
  const {
    categories,
    isLoading,
    showModal,
    editingCategory,
    formData,
    setFormData,
    openModal,
    closeModal,
    saveCategory,
    deleteCategory,
  } = useCommercialTerms();

  return (
    <section className="bg-white rounded-2xl border-2 border-slate-200/50 shadow-xl backdrop-blur-sm bg-gradient-to-br from-white via-purple-50/20 to-white">
      <div className="px-8 py-6 border-b-2 border-slate-200/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-purple-50/40 via-indigo-50/20 to-white">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-900">Commercial Terms Registry</h3>
            <p className="text-sm text-slate-600 mt-0.5 font-medium">Discount Groups & Credit Management</p>
          </div>
        </div>
        <button
          onClick={() => openModal()}
          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-purple-500/40 hover:shadow-xl hover:shadow-purple-500/50 transform hover:scale-105"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          New Group
        </button>
      </div>

      {isLoading ? (
        <div className="p-16 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl border-4 border-purple-200 border-t-purple-600 animate-spin shadow-lg"></div>
          <p className="text-sm text-slate-600 mt-4 font-semibold">Loading...</p>
        </div>
      ) : categories.length === 0 ? (
        <div className="p-16 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-100 to-indigo-100 mb-4 shadow-lg">
            <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-base font-semibold text-slate-800 mb-2">No discount groups created yet</p>
          <p className="text-sm text-slate-600">Create your first group to start managing customer discounts</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gradient-to-r from-purple-50 via-indigo-50/30 to-slate-50 text-slate-700 text-xs font-bold border-b-2 border-slate-200/50">
              <tr>
                <th className="px-6 py-4">Group Name</th>
                <th className="px-6 py-4">Charter Rebate</th>
                <th className="px-6 py-4">Schedule Rebate</th>
                <th className="px-6 py-4">Credit Limit</th>
                <th className="px-6 py-4">Customers</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100/50">
              {categories.map(cat => (
                <tr key={cat.id} className="hover:bg-gradient-to-r hover:from-purple-50/30 hover:via-indigo-50/20 hover:to-transparent transition-all group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-purple-500/30">
                        {cat.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-sm text-slate-900 font-semibold block">{cat.name}</span>
                        {cat.description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{cat.description}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-bold text-indigo-700">{cat.defaultCharterDiscount}%</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-bold text-emerald-700">{cat.defaultScheduleDiscount}%</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-mono font-bold text-slate-900">${Number(cat.defaultCreditLimit || 0).toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-100 text-slate-700 border-2 border-slate-200 shadow-sm">
                      {cat.customerCount || 0} customers
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-all">
                      <button
                        onClick={() => openModal(cat)}
                        className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-semibold hover:bg-indigo-100 hover:text-indigo-800 transition-all border border-indigo-200 shadow-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Delete discount group "${cat.name}"? This action cannot be undone.`)) {
                            deleteCategory(cat.id);
                          }
                        }}
                        className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 hover:text-red-800 transition-all border border-red-200 shadow-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-white rounded-2xl border-2 border-slate-300 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in zoom-in-95">
            <div className="bg-gradient-to-r from-purple-600 via-purple-600 to-indigo-600 text-white px-8 py-6 rounded-t-2xl flex justify-between items-center sticky top-0 z-10 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold">
                  {editingCategory ? 'Edit Discount Group' : 'Create Discount Group'}
                </h3>
              </div>
              <button onClick={closeModal} className="w-10 h-10 rounded-xl bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center text-white transition-all hover:scale-110">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-8 bg-gradient-to-br from-white via-purple-50/10 to-white">

            <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); saveCategory(); }}>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:shadow-md"
                  placeholder="e.g., VIP Customers"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:shadow-md"
                  placeholder="Brief description of this discount group"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Charter Rebate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.defaultCharterDiscount}
                    onChange={(e) => setFormData({ ...formData, defaultCharterDiscount: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Schedule Rebate (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.defaultScheduleDiscount}
                    onChange={(e) => setFormData({ ...formData, defaultScheduleDiscount: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Credit Limit (USD)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={formData.defaultCreditLimit}
                    onChange={(e) => setFormData({ ...formData, defaultCreditLimit: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-white border-2 border-slate-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all shadow-sm hover:shadow-md"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t-2 border-slate-200/50 mt-8">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 bg-white text-slate-700 rounded-xl text-sm font-semibold border-2 border-slate-300 hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm hover:shadow-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg shadow-purple-500/40 hover:shadow-xl hover:shadow-purple-500/50 transform hover:scale-105"
                >
                  {editingCategory ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
